import React, { Component, ReactNode } from 'react';
import StreamColumnEditorComponent from './StreamColumnEditorComponent';
import { RootState } from '../../../redux/store';
import { StreamState } from '../../../redux/reducers/StreamReducer';
import { changeStream, changeStreamIsUpdate } from '../../../redux/actions/StreamActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import StreamWhereComponent from './StreamWhereComponent';
import StreamKeyComponent from './StreamKeyComponent';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import StreamColumnCreatorComponent from './StreamColumnCreatorComponent';
import RoleHelper from '../../../RoleHelper';
import { AnimatedButton, Syntax } from '@trlogic/ui-pack';
import MessageUtil from '../../../util/MessageUtil';
import { StreamColumnJsonModel } from '../../../domain/stream/stream/StreamDomain';
import DataType from '../../../constant/DataType';
import { generateUUIDV4 } from 'util/Generator';
import ComponentName from '../../../constant/ComponentName';
import LanguageUtil from '../../../util/LanguageUtil';

class StreamEditorComponent extends Component<Props, State> {
  public state = new State();

  private ids: Record<string, StreamColumnJsonModel> = {};

  public render() {
    const { transactions, streamUpdated } = this.props;
    const { update } = this.state;
    return (
      <div className={'grid grid-cols-5 w-full h-full'}>
        <div className={'col-span-1 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-4'}>
          <StreamColumnCreatorComponent transactions={transactions} update={() => this.setState({ update: update + 1 })} />
        </div>
        <div className={'col-span-4 flex flex-col w-full h-full px-5 py-5 gap-y-4 overflow-y-scroll max-h-[calc(100vh-18.75rem)]'}>
          <StreamKeyComponent disabled={this.isExpressionDisabled()} update={this.state.update} streamUpdated={streamUpdated} />
          <div className={'flex gap-y-1 w-full justify-end'}>
            <AnimatedButton
              disabled={!RoleHelper.canEdit('Stream')}
              onClick={() => this.addColumn()}
              icon={'add'}
              customSize={'sm'}
              customType={'primary'}
              text={LanguageUtil.get({ id: `common.button.add`, defaultMessage: 'Add' })}
              data-testid={`${ComponentName.STREAM}_add_column_button`}
            />
          </div>
          {this.getStreamColumns()}
          <StreamWhereComponent disabled={this.isExpressionDisabled()} update={this.state.update} streamUpdated={streamUpdated} />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const {
      stream: { stream },
    } = this.props;
    stream.columns?.forEach((column) => {
      const id = generateUUIDV4();
      this.ids[id] = column;
    });
  }

  private getStreamColumns(): ReactNode {
    const {
      stream: { stream },
      streamUpdated,
      newColumnIds,
    } = this.props;
    return stream.columns?.map((column, index) => {
      const isNew = newColumnIds.includes(column.expression!.id);
      return (
        <div className={'flex items-center'} key={`${index}-${this.state.update}`}>
          <StreamColumnEditorComponent
            disabled={this.isExpressionDisabled()}
            isNew={isNew}
            column={column}
            streamUpdated={streamUpdated}
            onUpdate={() => this.setState({ update: this.state.update + 1 })}
          />
        </div>
      );
    });
  }

  private addColumn = () => {
    const { column } = this.state;
    const {
      stream: { stream },
      changeStream,
      newColumnIds,
      onNewColumnChanged,
    } = this.props;
    const _column: StreamColumnJsonModel = {
      name: column.name,
      properties: column.properties,
      expression: { id: generateUUIDV4(), value: { type: 'TokenGroup' }, children: [] },
    };
    newColumnIds.push(_column.expression!.id);
    stream?.columns?.push(_column);
    changeStream(stream);
    column.name = '';
    column.properties = { dataType: DataType.STRING };
    onNewColumnChanged();
    this.setState({ column: column });
  };

  private isExpressionDisabled = () => {
    const {
      stream: { stream },
    } = this.props;
    return stream.transaction == undefined || stream.transaction.length == 0;
  };
}

interface Props extends ReduxProps {
  transactions: TransactionJsonModel[];
  newColumnIds: string[];
  streamUpdated: number;
  onNewColumnChanged: VoidFunction;
}

class State {
  update: number = 0;
  column: StreamColumnJsonModel = {
    name: '',
    properties: { dataType: DataType.STRING },
    expression: {} as Syntax,
  };
}

const mapStateToProps = (state: RootState) => ({
  stream: state.stream as StreamState,
  lastCategory: state.page.lastCategory,
});

const mapDispatchToProps = { changeStream, changeStreamIsUpdate };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(StreamEditorComponent);
